import type { Cents } from "../domain/money";
import type { FinancialBucket } from "../domain/buckets";
import type { ExpenseCategory } from "../domain/category";
import type { AcquisitionCostLine } from "../domain/acquisition";
import type { ProposedCostLine } from "../domain/proposedCostLine";
import type { ActualCostEvent } from "./actualEvents";
import { MILEAGE_CATEGORY_ID } from "./actualEvents";

/**
 * Authoritative project financial calculations.
 *
 * Pure functions over integer cents — no I/O, no database, no UI. Every
 * financial number displayed anywhere in Berrier Reno comes from here
 * (master spec Section 7). UI components must never re-implement these.
 *
 * Since Phase 5 the actual side operates on the normalized
 * ActualCostEvent list (manual expenses + purchase allocations + mileage,
 * see actualEvents.ts) so there is ONE bucket/variance/linking
 * implementation for all sources.
 */

export interface ProjectFinancialsInput {
  purchasePriceCents: Cents | null;
  contingencyCents: Cents | null;
  acquisitionLines: AcquisitionCostLine[];
  proposedLines: ProposedCostLine[];
  /** All actual-cost events for THIS project (all sources). */
  events: ActualCostEvent[];
  categories: ExpenseCategory[];
}

export interface ProjectFinancialSummary {
  purchasePriceCents: Cents | null;
  acquisitionExpensesCents: Cents;
  totalAcquisitionCostCents: Cents;
  proposedRehabCents: Cents;
  contingencyCents: Cents | null;
  totalAuthorizedRehabBudgetCents: Cents;
  bucketTotals: Record<FinancialBucket, Cents>;
  /** Σ mileage events (already inside bucketTotals; separately identifiable). */
  mileageCents: Cents;
  /** Σ manual-expense events, Σ allocation events — for source breakdowns. */
  sourceTotals: { expenseCents: Cents; allocationCents: Cents; mileageCents: Cents };
  totalProjectSpendCents: Cents;
  rehabVarianceToDateCents: Cents;
}

/** An event's total cost: pre-tax amount + actual sales tax. */
export function expenseTotalCents(e: Pick<ActualCostEvent, "amountCents" | "taxCents">): Cents {
  return e.amountCents + e.taxCents;
}

/**
 * Effective Financial Bucket: the event-specific override when present,
 * otherwise the category's default bucket. Unknown categories fall back to
 * 'other' rather than losing money from totals.
 */
export function effectiveBucket(
  event: Pick<ActualCostEvent, "bucketOverride" | "categoryId">,
  categoriesById: Map<string, ExpenseCategory>,
): FinancialBucket {
  if (event.bucketOverride !== null) return event.bucketOverride;
  return categoriesById.get(event.categoryId)?.defaultBucket ?? "other";
}

export function categoriesById(categories: ExpenseCategory[]): Map<string, ExpenseCategory> {
  return new Map(categories.map((c) => [c.id, c]));
}

/** Σ non-archived, non-credited acquisition line amounts. */
export function acquisitionExpensesCents(lines: AcquisitionCostLine[]): Cents {
  return lines
    .filter((l) => l.archivedAt === null && !l.creditedTowardPurchase)
    .reduce((sum, l) => sum + l.amountCents, 0);
}

/** Σ active (non-archived) proposed line amounts. Pre-contingency. */
export function proposedRehabCents(lines: ProposedCostLine[]): Cents {
  return lines.filter((l) => l.archivedAt === null).reduce((sum, l) => sum + l.amountCents, 0);
}

/** Non-void event totals grouped by effective financial bucket. */
export function actualBucketTotals(
  events: ActualCostEvent[],
  categories: ExpenseCategory[],
): Record<FinancialBucket, Cents> {
  const byId = categoriesById(categories);
  const totals: Record<FinancialBucket, Cents> = {
    acquisition: 0,
    renovation: 0,
    holding: 0,
    financing: 0,
    selling: 0,
    other: 0,
  };
  for (const e of events) {
    if (e.voidedAt !== null) continue;
    totals[effectiveBucket(e, byId)] += expenseTotalCents(e);
  }
  return totals;
}

/** Σ non-void events linked directly to one proposed line. */
export function linkedActualCents(lineId: string, events: ActualCostEvent[]): Cents {
  return events
    .filter((e) => e.voidedAt === null && e.linkedProposedLineId === lineId)
    .reduce((sum, e) => sum + expenseTotalCents(e), 0);
}

/** Linked Actual − Proposed Amount, for one proposed line. */
export function lineVarianceCents(line: ProposedCostLine, events: ActualCostEvent[]): Cents {
  return linkedActualCents(line.id, events) - line.amountCents;
}

/**
 * Feature Actual: events linked DIRECTLY to the feature, plus events
 * linked to Proposed Cost Lines belonging to the feature. The two direct
 * link kinds are mutually exclusive on an event, so every dollar is
 * counted exactly once — across manual expenses, allocations, and returns.
 */
export function featureActualCents(
  featureId: string,
  proposedLines: Pick<ProposedCostLine, "id" | "featureId">[],
  events: ActualCostEvent[],
): Cents {
  const featureLineIds = new Set(
    proposedLines.filter((l) => l.featureId === featureId).map((l) => l.id),
  );
  return events
    .filter(
      (e) =>
        e.voidedAt === null &&
        (e.linkedFeatureId === featureId ||
          (e.linkedProposedLineId !== null && featureLineIds.has(e.linkedProposedLineId))),
    )
    .reduce((sum, e) => sum + expenseTotalCents(e), 0);
}

/** The complete financial summary for one project. */
export function computeProjectFinancials(input: ProjectFinancialsInput): ProjectFinancialSummary {
  const acqExpenses = acquisitionExpensesCents(input.acquisitionLines);
  const totalAcquisition = (input.purchasePriceCents ?? 0) + acqExpenses;
  const proposedRehab = proposedRehabCents(input.proposedLines);
  const totalAuthorized = proposedRehab + (input.contingencyCents ?? 0);
  const live = input.events.filter((e) => e.voidedAt === null);
  const buckets = actualBucketTotals(live, input.categories);
  const sum = (src: ActualCostEvent["source"]) =>
    live.filter((e) => e.source === src).reduce((s, e) => s + expenseTotalCents(e), 0);
  const mileageCents = live
    .filter((e) => e.source === "mileage" || e.categoryId === MILEAGE_CATEGORY_ID)
    .reduce((s, e) => s + expenseTotalCents(e), 0);

  const totalSpend =
    totalAcquisition +
    buckets.acquisition +
    buckets.renovation +
    buckets.holding +
    buckets.financing +
    buckets.selling +
    buckets.other;

  return {
    purchasePriceCents: input.purchasePriceCents,
    acquisitionExpensesCents: acqExpenses,
    totalAcquisitionCostCents: totalAcquisition,
    proposedRehabCents: proposedRehab,
    contingencyCents: input.contingencyCents,
    totalAuthorizedRehabBudgetCents: totalAuthorized,
    bucketTotals: buckets,
    mileageCents,
    sourceTotals: {
      expenseCents: sum("expense"),
      allocationCents: sum("allocation"),
      mileageCents: sum("mileage"),
    },
    totalProjectSpendCents: totalSpend,
    rehabVarianceToDateCents: buckets.renovation - proposedRehab,
  };
}
